// File: src/pages/VendorPaymentsPage.jsx

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Plus, FileText } from 'lucide-react';
import VendorPaymentModal from "@/components/Procurement/VendorPaymentModal";

const VendorPaymentsPage = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [totalsByVendor, setTotalsByVendor] = useState({});

  const [formData, setFormData] = useState({
    vendor_id: '',
    amount: '',
    payment_method: '',
    notes: '',
    paid_by: ''
  });

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/solo-mdsh-api/vendor-payments');
      const data = res.data || [];
      setPayments(data);
      calculateTotals(data);
    } catch (err) {
      console.error('Failed to load vendor payments:', err);
      setError('Failed to load vendor payments.');
    } finally {
      setLoading(false);
    }
  };

  const calculateTotals = (payments) => {
    const totals = {};
    payments.forEach((p) => {
      if (!totals[p.vendor_name]) totals[p.vendor_name] = 0;
      totals[p.vendor_name] += parseFloat(p.amount);
    });
    setTotalsByVendor(totals);
  };

  const handleSubmit = async () => {
    const { vendor_id, amount, paid_by } = formData;

    if (!vendor_id || !amount || !paid_by) {
      setError('⚠️ Vendor, amount and paid by are required.');
      return;
    }

    try {
      const res = await axios.post('/solo-mdsh-api/vendor-payments', formData);
      setMessage(res.data.message || '✅ Payment recorded.');
      setError('');
      setModalOpen(false);
      setFormData({
        vendor_id: '',
        amount: '',
        payment_method: '',
        notes: '',
        paid_by: ''
      });
      fetchPayments();
    } catch (err) {
      console.error('Payment submission failed:', err);
      setError('❌ Failed to record payment.');
    }
  };

  return (
    <div className="min-h-screen p-8 text-white">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">💸 Vendor Payments</h1>
        <div className="flex gap-2">
          <button
            onClick={() => window.open('/solo-mdsh-api/vendor-payments/export-pdf', '_blank')}
            className="flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-xl shadow"
          >
            <FileText size={16} /> Export PDF
          </button>
          <button
            onClick={() => setModalOpen(true)}
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-xl shadow"
          >
            <Plus size={16} /> New Payment
          </button>
        </div>
      </div>

      {Object.keys(totalsByVendor).length > 0 && (
        <div className="mb-4 space-y-1 text-sm">
          <h2 className="font-semibold text-white">📊 Totals by Vendor:</h2>
          {Object.entries(totalsByVendor).map(([vendor, total]) => (
            <div key={vendor} className="text-gray-300">
              {vendor}: <span className="font-bold text-green-400">£{total.toFixed(2)}</span>
            </div>
          ))}
        </div>
      )}

      {message && <div className="text-green-400 mb-4">{message}</div>}
      {error && <div className="text-red-400 mb-4">{error}</div>}

      {loading ? (
        <div className="text-blue-300 flex items-center gap-2">
          <Loader2 className="animate-spin" /> Loading payments...
        </div>
      ) : payments.length === 0 ? (
        <p className="text-yellow-400">⚠️ No vendor payments recorded yet.</p>
      ) : (
        <div className="overflow-x-auto bg-white/5 border border-white/10 rounded-xl">
          <table className="min-w-full text-sm text-white">
            <thead className="bg-gray-900">
              <tr>
                <th className="px-4 py-3 text-left">Vendor</th>
                <th className="px-4 py-3 text-left">Amount</th>
                <th className="px-4 py-3 text-left">Method</th>
                <th className="px-4 py-3 text-left">Date</th>
                <th className="px-4 py-3 text-left">Paid By</th>
                <th className="px-4 py-3 text-left">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {payments.map((p) => (
                <tr key={p.settlement_id}>
                  <td className="px-4 py-2">{p.vendor_name}</td>
                  <td className="px-4 py-2">£{p.amount.toFixed(2)}</td>
                  <td className="px-4 py-2">{p.payment_method || '—'}</td>
                  <td className="px-4 py-2">{new Date(p.payment_date).toLocaleDateString()}</td>
                  <td className="px-4 py-2">{p.staff_name || '—'}</td>
                  <td className="px-4 py-2">{p.notes || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <VendorPaymentModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleSubmit}
      />
    </div>
  );
};

export default VendorPaymentsPage;
